import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, find, assign, cloneDeep, set } from 'lodash';
import Contracts from '../../../api/api-contracts';
import { Spinner } from 'shared-components/decorators/spinner';
import CancellationPolicy from './cancellation-policy';
import CancelBookingForm from './forms/cancel-booking-form';
import {
  CancelSummaryCalculator,
  CancelContractSummaryCalculator,
  CancelSummaryCalculatorFnb
} from './summary-cancel-detail';
import { updateProgress } from '../app-main/actions/flow';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import SharedIntlProviderHOC from '../shared-components/hocs/sharedIntlProviderHOC';
import { BookingSummary } from '../elements-summaries/booking-summary/BookingSummary';

class CancelBookingMain extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      displayError: '',
      isProcessing: false
    };

    this.analyticsPayload = this.analyticsPayload.bind(this);
    this.submitCancellation = this.submitCancellation.bind(this);
    this.submitCancellationCb = this.submitCancellationCb.bind(this);
    this.bookingSummaryDisplay = this.bookingSummaryDisplay.bind(this);
    this.backAction = this.backAction.bind(this);
    this.creditIncentiveAction = this.creditIncentiveAction.bind(this);
  }

  componentDidMount() {
    const { displayOptionsPage } = this.props;

    const payload = this.analyticsPayload(null, 'Cancel Booking Viewed');
    payload.origin = displayOptionsPage ? 'cancellation_options' : 'inbox';

    analyticsIntegrations.cancelBooking('Cancel Booking Viewed', payload);
  }

  componentDidUpdate(prevProps) {
    const { flowStep } = this.props;

    // fire analytic here if incentive banner is clicked since component does not mount again
    if (
      'standardRefund' === prevProps.flowStep &&
      'creditRefund' === flowStep
    ) {
      const payload = this.analyticsPayload(null, 'Cancel Booking Viewed');
      payload.origin = 'cancel_booking';

      analyticsIntegrations.cancelBooking('Cancel Booking Viewed', payload);
    }
  }

  analyticsPayload(reason, action) {
    const { cancelContractObj = {}, cancelDetails = {}, flowStep } = this.props;

    const spaceRentalContractCancellation = find(cancelContractObj.contracts, {
      type: 'SPACE_RENTAL'
    });
    const booking = get(this.props, 'booking.booking', {});
    const currency = get(booking, 'listing.prices[0].currency');

    const guestRefundAmounts = get(cancelContractObj, 'delta.guest', {});
    const spaceRentalRefundAmounts = get(
      cancelContractObj,
      'delta.space_rentals',
      {}
    );
    const incentiveEligible = get(
      cancelContractObj,
      'incentive_credit_eligible',
      false
    );

    const {
      cleaning_fee: addtl_charges,
      fee: fee_total,
      fee_percent,
      payment: total
    } = guestRefundAmounts;

    const {
      base_subtotal,
      gratuity,
      sales_tax,
      guest_subtotal_payment_amount: subtotal
    } = spaceRentalRefundAmounts;

    const {
      is_custom_cancel: custom,
      is_old_policy_cancel,
      is_full_refund
    } = cancelDetails;

    const payload = {
      booking_id: booking._id,
      initiated_by: 'guest',
      eligible_for_credit_bonus: incentiveEligible,
      credit_bonus_pct_shown: incentiveEligible ? 20 : 0,
      page_type:
        'creditRefund' === flowStep ? 'cancel for credit' : 'cancel booking'
    };

    const submittedPayload = {
      cancel_reason: reason,
      booking_target_date: booking.schedule,
      cancel_date: new Date(),
      origin:
        'creditRefund' === flowStep ? 'cancel_for_credit' : 'cancel_booking'
    };

    const confirmedPayload = {
      auto_cancel: is_old_policy_cancel && is_full_refund,
      percent_refund: cancelDetails.refund_percentage,
      amount_refund: {
        addtl_charges,
        add_ons: get(guestRefundAmounts, 'add_ons.amount', 0),
        base_rate: booking.base_rate,
        base_subtotal,
        currency,
        custom,
        discounts: get(
          spaceRentalContractCancellation,
          'meta.booking.discounts',
          [{}]
        ),
        fee_percent,
        fee_total,
        gratuity,
        rate: booking.rate,
        sales_tax,
        subtotal,
        total
      }
    };

    switch (action) {
      case 'Cancel Booking Viewed':
        return payload;
      case 'Cancel Booking Submitted':
        return assign(payload, submittedPayload);
      case 'Cancel Booking Confirmation':
        return assign(payload, submittedPayload, confirmedPayload);
      default:
        return payload;
    }
  }

  submitCancellation(formValues, formCb) {
    this.setState({ isProcessing: true });
    const { accessToken, booking, flowStep } = this.props;
    const bookingId = get(booking, 'booking._id', '');
    const creditOptionSelected = 'creditRefund' === flowStep;

    const payload = {
      credits_requested: creditOptionSelected,
      private_reason: formValues.otherReason || formValues.cancelReason,
      reason: formValues.messageToHost
    };

    const analyticsPayload = this.analyticsPayload(
      payload.private_reason,
      'Cancel Booking Submitted'
    );
    analyticsIntegrations.cancelBooking(
      'Cancel Booking Submitted',
      analyticsPayload
    );

    Contracts.cancelBookingContract(
      payload,
      'guest',
      bookingId,
      accessToken,
      (response) => {
        formCb(response, payload.private_reason);
      }
    );
  }

  submitCancellationCb(response, reason) {
    if (200 === response.statusCode) {
      const payload = this.analyticsPayload(
        reason,
        'Cancel Booking Confirmation'
      );
      analyticsIntegrations.cancelBooking(
        'Cancel Booking Confirmation',
        payload
      );

      if (this.props.finalCb) {
        this.props.closeIOSModal();
        this.props.finalCb();
        return null;
      }

      const masterContract = find(response.contracts, { type: 'MASTER' }) || {};
      const bookingID = masterContract.control_id;
      return this.context.router.push({ pathname: `/inbox/${bookingID}` });
    } else if (403 === response.statusCode) {
      return this.setState({
        displayError: response.error,
        modalDisplayError: response.error,
        isProcessing: false
      });
    }

    return this.setState({
      displayError: this.props.intl.formatMessage({
        id: 'inbox_id_cancel_booking.cancel_booking_main.something_wrong',
        defaultMessage: 'Something went wrong, please try again.'
      }),
      modalDisplayError: this.props.intl.formatMessage({
        id: 'inbox_id_cancel_booking.cancel_booking_main.something_wrong_modal',
        defaultMessage: 'Something went wrong, please try again.'
      }),
      isSubmittingCancelModal: false,
      isProcessing: false
    });
  }

  bookingSummaryDisplay() {
    const {
      contracts,
      cancelContractObj = {},
      flowStep,
      cancelDetails = {}
    } = this.props;
    const booking = get(this.props, 'booking.booking', {});

    const currencyType = get(booking, 'listing.prices[0].currency');
    const paymentDetails = get(cancelContractObj, 'payment_changes', {});
    const creditRefundSelected = 'creditRefund' === flowStep;

    if (isEmpty(cancelContractObj)) {
      return;
    }

    if (!cancelDetails.is_old_policy_cancel || cancelDetails.is_full_refund) {
      return (
        <div>
          <h5>Booking Summary</h5>
          {!isEmpty(contracts) ? (
            <CancelContractSummaryCalculator
              contractObj={contracts}
              refundContractObj={cancelContractObj}
              currencyType={currencyType}
              paymentDetails={paymentDetails}
              creditRefundSelected={creditRefundSelected}
            />
          ) : 'FANDB' === booking.type ? (
            <CancelSummaryCalculatorFnb
              bookingObj={booking}
              refundBookingObj={cancelContractObj}
              currencyType={currencyType}
            />
          ) : (
            <CancelSummaryCalculator
              bookingObj={booking}
              refundBookingObj={cancelContractObj}
              currencyType={currencyType}
            />
          )}
        </div>
      );
    }

    return (
      <p>
        <FormattedMessage
          id='inbox_id_cancel_booking.cancel_booking_main.cancellation_request'
          defaultMessage='This cancellation request will be under review and you will be contacted by Peerspace customer support.'
        />
      </p>
    );
  }

  backAction() {
    const {
      closeIOSModal,
      booking,
      dispatch,
      scrollTopIOS,
      displayOptionsPage
    } = this.props;
    const bookingId = get(booking, 'booking._id', '');

    if (displayOptionsPage) {
      if (scrollTopIOS) {
        scrollTopIOS();
      }
      window.scrollTo(0, 0);
      return dispatch(updateProgress('refundOptions'));
    }

    if (closeIOSModal) {
      return closeIOSModal();
    }

    this.context.router.push({ pathname: `/inbox/${bookingId}` });
  }

  creditIncentiveAction() {
    this.props.selectCancelAction(true);
  }

  render() {
    const { displayError, isProcessing } = this.state;
    const {
      cancelContractObj = {},
      contractsError,
      flowStep,
      cancelDetails = {},
      isPosting = false,
      profile,
      intl
    } = this.props;
    const booking = get(this.props, 'booking.booking', {});
    const creditRefundSelected = 'creditRefund' === flowStep;
    const paymentDetails = get(cancelContractObj, 'payment_changes', {});

    const activityDetailBooking = cloneDeep(booking);
    if (!isEmpty(activityDetailBooking)) {
      activityDetailBooking.payment = {};
      activityDetailBooking.list_price = {};
      activityDetailBooking.payout = {};
      activityDetailBooking.view_as = 'GUEST';
      activityDetailBooking.listing.use_type = null;
      set(activityDetailBooking, 'guest.sso_id', get(booking, 'guest.ssoid'));
    }
    const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
    const prefix = SharedIntlProviderHOC.getUrlPrefix();
    const urlPrefix = prefix ? `/${prefix}` : '';

    return (
      <div className='page' data-testing-id='cancel-booking-main'>
        {isPosting ? (
          <div className='spinner-container'>
            <Spinner additionalStyle='gray' />
          </div>
        ) : (
          <div>
            <div className='container-fluid no-gutter'>
              <div className='row gutter'>
                <div className='col-xs-12 col-md-5 col-lg-offset-2 col-md-offset-1'>
                  <h3>
                    <FormattedMessage
                      id='inbox_id_cancel_booking.cancel_booking_main.cancel_booking'
                      defaultMessage='Cancel Booking {refund}'
                      values={{
                        refund: creditRefundSelected ? (
                          <FormattedMessage
                            id='inbox_id_cancel_booking.cancel_booking_main.for_credit'
                            defaultMessage='for Credit'
                          />
                        ) : (
                          ''
                        )
                      }}
                    />
                  </h3>
                  <p className='cancelation-text'>
                    <FormattedMessage
                      id='inbox_id_cancel_booking.cancel_booking_main.review_policy'
                      defaultMessage='Please review our {cancellationPolicy} for more information. If you have any questions about the cancellation process, please contact our support team {link}.'
                      values={{
                        cancellationPolicy: (
                          <a
                            href={`https://www.peerspace.com${urlPrefix}/legal/terms/cancellation-policy`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <FormattedMessage
                              id='inbox_id_cancel_booking.cancel_booking_main.cancellation_refund_policy'
                              defaultMessage='Cancellation and Refund Policy'
                            />
                          </a>
                        ),
                        link: <a target="_blank" className="underline" href={`https://support.peerspace.com/hc/${zendeskLocale}/requests/new`}>
                          <FormattedMessage id='inbox_id_cancel_booking.cancel_booking_main.here' defaultMessage='here' />
                        </a>
                      }}
                    />
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className='container-fluid'>
                <div className='row activity-detail-container'>
                  <div className='col-xs-12 col-md-6 col-md-push-1 col-lg-5 col-lg-push-2 margin-lg-top margin-lg-bottom'>
                    <CancelBookingForm
                      {...this.props}
                      bookingSummaryDisplay={this.bookingSummaryDisplay}
                      displayError={displayError || contractsError}
                      backAction={this.backAction}
                      creditIncentiveAction={this.creditIncentiveAction}
                      submitCancellation={this.submitCancellation}
                      submitCancellationCb={this.submitCancellationCb}
                      isProcessing={isProcessing}
                      paymentDetails={paymentDetails}
                    />
                  </div>

                  <div className='col-xs-12 col-md-4 col-md-push-1 col-lg-3 col-lg-push-2 margin-lg-top margin-lg-bottom'>
                    <BookingSummary
                      booking={activityDetailBooking}
                      profile={profile}
                      intl={intl}
                    />
                    {isEmpty(cancelContractObj) ||
                    cancelDetails.is_old_policy_cancel ? null : (
                      <CancellationPolicy
                        cancelUpdateObj={cancelDetails}
                        pageView='cancelForm'
                        viewAs='guest'
                        isFnb={'FANDB' === booking.type}
                        creditRefundSelected={creditRefundSelected}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

CancelBookingMain.propTypes = {
  booking: PropTypes.object,
  contracts: PropTypes.array,
  accessToken: PropTypes.string,
  closeIOSModal: PropTypes.func,
  finalCb: PropTypes.func,
  cancelContractObj: PropTypes.object,
  intl: intlShape
};

CancelBookingMain.contextTypes = {
  router: PropTypes.object
};

exports.CancelBookingMain = injectIntl(CancelBookingMain);
